import React, { useEffect, useContext } from 'react';
import ResponsiveSidebar from '@Components/LeftSidebar';
import { Outlet, useNavigate } from 'react-router-dom';
import { alpha, styled } from '@mui/material/styles';
import { getOrganizationDetails, setOrgCountry } from '@Reducers/OrganizationSetup';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootStateType } from '@Store';
import { organizationSetupRoute } from '@Constants/route';
import { useOrgSetupStatus } from '@Hook/useOrgSetupStatus';
import Header from '@Components/Header';
import SuccessError from '@Components/Modal/SuccessError';
import CONFIG from '../config';
import SpeedboxChatBot from '@Components/Bot/SpeedboxChatBot';
import { MenuContext } from '../context/Menu';

const Home: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const { pickupPinCode } = useSelector((state: RootStateType) => state.getRates.formData);
  const { isBookNow_WithAuth } = useSelector((state: RootStateType) => state.getRates);

  const orgPublicData = useSelector((state: RootStateType) => state.orgSetup.organizationPublicData);
  const countriesObj = useSelector((state: RootStateType) => state.global.countriesObj);

  const orgData = useSelector((state: RootStateType) => state.orgSetup);
  const { organizationData } = orgData;

  const { orgCurrentLevel } = useOrgSetupStatus();
  const { isChatbotOpen } = useContext(MenuContext);

  const handleNavigate = (url: string) => {
    navigate(url);
  };

  // redirect create shipment
  useEffect(() => {
    if (pickupPinCode && !isBookNow_WithAuth) {
      navigate('/shipment/create-single');
    }
  }, []);

  // set country
  useEffect(() => {
    if (!orgPublicData?._countryId) return;
    const country = countriesObj[orgPublicData._countryId];
    dispatch(setOrgCountry(country));
  }, [countriesObj, orgPublicData?._countryId, dispatch]);

  // fetch org details
  useEffect(() => {
    dispatch(getOrganizationDetails());
  }, [dispatch]);

  // org level routing
  useEffect(() => {
    if (orgCurrentLevel !== 'shipment-settings') {
      navigate(organizationSetupRoute[orgCurrentLevel]);
    }
  }, [orgCurrentLevel, navigate]);

  if (orgCurrentLevel !== 'shipment-settings') return <Outlet />;

  const showChatbot = organizationData?.enableBot;

  return (
    <Layout>
      <ResponsiveSidebar />
      <MainSection>
        <Header />
        <ContentContainer>
          <Content>
            <Outlet />
          </Content>
          {showChatbot && (
            <CopilotLayoutWrapper open={isChatbotOpen}>
              <SpeedboxChatBot
                orgCode="stage"
                botEnabled={true}
                onNavigate={handleNavigate}
                apiBaseUrl={CONFIG.API_URI}
              />
            </CopilotLayoutWrapper>
          )}
        </ContentContainer>
      </MainSection>
      <SuccessError />
    </Layout>
  );
};

export default Home;

/* ================= STYLES ================= */

const Layout = styled('section')(({ theme }) => ({
  display: 'flex',
  height: 'var(--layout-height)',
  overflow: 'hidden',
  padding: '10px',
  gap: '10px',
  backgroundColor: theme.palette.mode === 'light' ? '#f5f7fa' : '#0a1929',
}));

const MainSection = styled('div')(() => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
}));

const Content = styled('div')(({ theme }) => ({
  flex: 1,
  overflowY: 'auto',
  padding: '24px',
  borderRadius: '16px',

  // Glassmorphism effect
  background: alpha(theme.palette.background.paper, 0.7),
  backdropFilter: 'blur(20px)',
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  boxShadow: `0 8px 32px 0 ${alpha(theme.palette.common.black, 0.08)}`,
}));

const ContentContainer = styled('div')(() => ({
  flex: 1,
  display: 'flex',
  overflow: 'hidden',
  marginTop: '10px',
}));

const CopilotLayoutWrapper = styled('div', {
  shouldForwardProp: prop => prop !== 'open',
})<{ open: boolean }>(({ theme, open }) => ({
  // On mobile, the wrapper has 0 footprint but allows fixed children to be visible
  [theme.breakpoints.down('md')]: {
    width: 0,
    minWidth: 0,
    height: 0,
    overflow: 'visible',
  },
  // On desktop, it takes space if open
  [theme.breakpoints.up('md')]: {
    width: open ? '360px' : '0px',
    minWidth: open ? '360px' : '0px',
    marginLeft: open ? '10px' : '0px',
    transition:
      'width 0.3s cubic-bezier(0.4, 0, 0.2, 1), min-width 0.3s cubic-bezier(0.4, 0, 0.2, 1), margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    height: '100%',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
}));
